import classNames from "classnames";

type FeatureIconProps = {
  color?: keyof typeof colors;
  variant?: keyof typeof variants;
  size?: keyof typeof sizes;
} & React.HTMLAttributes<HTMLDivElement>;

const variants = {
  solid: "rounded-full",
  double: "rounded-full border-4",
  outline: "rounded-full",
  modern: "border shadow-xs rounded-xl",
};

const colors = {
  primary: {
    solid: "text-primary-600 bg-primary-100",
    double: "text-primary-600 bg-primary-100 border-primary-50",
    outline: "text-primary-600 border-primary-600/30 outline-primary-600/10",
    modern: "border-primary-200",
  },
  warning: {
    solid: "text-warning-600 bg-warning-100",
    double: "text-warning-600 bg-warning-100 border-warning-50",
    outline: "text-warning-600 border-warning-600/30 outline-warning-600/10",
    modern: "border-warning-200",
  },
  success: {
    solid: "text-success-600 bg-success-100",
    double: "text-success-600 bg-success-100 border-success-50",
    outline: "text-success-600 border-success-600/30 outline-success-600/10",
    modern: "border-success-200",
  },
  greenlight: {
    solid: "text-greenlight-600 bg-greenlight-100",
    double: "text-greenlight-600 bg-greenlight-100 border-greenlight-50",
    outline:
      "text-greenlight-600 border-greenlight-600/30 outline-greenlight-600/10",
    modern: "border-greenlight-200",
  },
  error: {
    solid: "text-error-600 bg-error-100",
    double: "text-error-600 bg-error-100 border-error-50",
    outline: "text-error-600 border-error-600/30 outline-error-600/10",
    modern: "border-error-200",
  },
  blue: {
    solid: "text-blue-600 bg-blue-100",
    double: "text-blue-600 bg-blue-100 border-blue-50",
    outline: "text-blue-600 border-blue-600/30 outline-blue-600/10",
    modern: "border-blue-200",
  },
  pink: {
    solid: "text-pink-600 bg-pink-100",
    double: "text-pink-600 bg-pink-100 border-pink-50",
    outline: "text-pink-600 border-pink-600/30 outline-pink-600/10",
    modern: "border-pink-200",
  },
  gray: {
    solid: "text-gray-600 bg-gray-100",
    double: "text-gray-600 bg-gray-100 border-gray-50",
    outline: "text-gray-600 border-gray-600/30 outline-gray-600/10",
    modern: "border-gray-200",
  },
  white: {
    solid: "text-gray-700",
    double: "text-white border-gray-50",
    outline: "text-white border-white/30 outline-white/10",
    modern: "border-white/30",
  },
};

const sizes = {
  sm: {
    solid: "p-2",
    double: "p-1",
    outline: "border-2 p-0.5 outline-2 outline-offset-[3px] -m-1",
    modern: "p-2 rounded-md",
  },
  md: {
    solid: "p-2.5",
    double: "p-1.5",
    outline: "border-2 p-0.5 outline-2 outline-offset-[3px] -m-1",
    modern: "p-2.5 rounded-lg",
  },
  lg: {
    solid: "p-3",
    double: "p-2",
    outline: "border-2 p-0.5 outline-2 outline-offset-[3px] -m-1",
    modern: "p-3 rounded-[10px]",
  },
  xl: {
    solid: "p-3.5",
    double: "p-2.5",
    outline: "border-2 p-0.5 outline-2 outline-offset-[3px] -m-1",
    modern: "p-3.5 rounded-xl",
  },
};

export default function FeatureIcon({
  color = "gray",
  variant = "solid",
  size = "md",
  children,
  className,
  ...rest
}: FeatureIconProps) {
  return (
    <div
      className={classNames(
        variants[variant],
        colors[color][variant],
        sizes[size][variant],
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  );
}
